import React, { useState, useEffect, useRef } from 'react';
import './UpcomingTraining.css';
import { supabase } from '../supabase';

const UpcomingTraining = () => {
  const [activeTraining, setActiveTraining] = useState(0);
  const [counters, setCounters] = useState({ dossiers: 0, sectors: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  // Registration modal states
  const [showRegistration, setShowRegistration] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [regData, setRegData] = useState({
    fullName: '',
    email: '',
    phone: '',
    institution: '',
    sector: '',
    institutionContactPerson: '',
    position: '',
    country: '',
    agreeTerms: false
  });
  const [regLoading, setRegLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ title: '', message: '', type: '' });

  const trainings = [
    {
      id: 1,
      title: 'Africa-Continental Project Development & Financing: Green Investment Workshop',
      strategicObjective: 'Prepare finance-ready continental projects aligned with Nigeria Infrastructure Master Plan, PIDA, AfCFTA, AU Climate Strategy and Africa Carbon Market initiatives. Ensure bankability and ESG compliance.',
      venue: 'Afreximbank Africa Trade Centre (AATC), Abuja',
      date: '7–12 December 2026',
      sectors: ['Renewable Energy', 'Transport Corridors', 'Industrial Parks', 'Carbon Markets', 'Security Infrastructure'],
      outcomes: '15–25 Investment-Ready Dossiers',
      fee: '$1,700',
      includes: ['Breakfast & Lunch', 'Original Certificate', 'Training Materials', 'Executive Accommodation', 'Tax Applicable'],
      facilitators: ['Prof. Ladan M.T.', 'Prof. Sunday', 'Prof. Chris', 'World Bank'],
      participants: ['Private Sector', 'International Agencies', 'Government Officials', 'CSO/NGO', 'Politicians', 'Academia', 'Diplomatic Corps'],
      schedule: [
        { day: 'Monday, 7 December', activity: 'Arrival & Registration' },
        { day: 'Tuesday, 8 December', activity: 'Opening Ceremony & Technical Session' },
        { day: 'Wednesday, 9 December', activity: 'Technical Session (Day 1)' },
        { day: 'Thursday, 10 December', activity: 'Technical Session (Day 2)' },
        { day: 'Friday, 11 December', activity: 'Site Visit: Dangote Refinery' },
        { day: 'Saturday, 12 December', activity: 'Departure' }
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;
      let currentStep = 0;

      const animateCounter = () => {
        if (currentStep < steps) {
          const progress = currentStep / steps;
          const eased = 1 - Math.pow(1 - progress, 3);
          setCounters({
            dossiers: Math.round(eased * 25),
            sectors: Math.round(eased * 5)
          });
          currentStep++;
          setTimeout(animateCounter, stepTime);
        } else {
          setCounters({ dossiers: 25, sectors: 5 });
        }
      };

      animateCounter();
    }
  }, [isVisible]);

  const toggleTraining = (index) => {
    setActiveTraining(activeTraining === index ? -1 : index);
  };

  const openRegistration = (training) => {
    setSelectedTraining(training);
    setShowRegistration(true);
  };

  const closeRegistration = () => {
    setShowRegistration(false);
    setSelectedTraining(null);
    setRegData({
      fullName: '',
      email: '',
      phone: '',
      institution: '',
      sector: '',
      institutionContactPerson: '',
      position: '',
      country: '',
      agreeTerms: false
    });
  };

  const handleRegChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegData({
      ...regData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    setRegLoading(true);

    // Validate required fields
    const required = ['fullName', 'email', 'phone', 'institution', 'sector', 'institutionContactPerson', 'position', 'country'];
    for (const field of required) {
      if (!regData[field]) {
        setPopupMessage({
          title: '⚠️ Incomplete Form',
          message: 'Please fill in all required fields.',
          type: 'error'
        });
        setShowPopup(true);
        setRegLoading(false);
        return;
      }
    }

    try {
      const { error } = await supabase
        .from('training_registrations')
        .insert([{
          training_title: selectedTraining.title,
          training_date: selectedTraining.date,
          training_venue: selectedTraining.venue,
          full_name: regData.fullName,
          email: regData.email,
          phone: regData.phone,
          institution: regData.institution,
          sector: regData.sector,
          institution_contact_person: regData.institutionContactPerson,
          position: regData.position,
          country: regData.country,
          status: 'pending'
        }]);

      if (error) throw error;

      setPopupMessage({
        title: '✅ Registration Successful!',
        message: 'Your registration has been received. Our team will contact you within 48 hours with payment and participation details.',
        type: 'success'
      });
      setShowPopup(true);
      closeRegistration();
    } catch (error) {
      console.error('Error submitting registration:', error);
      setPopupMessage({
        title: '❌ Registration Failed',
        message: 'There was a problem submitting your registration. Please try again or contact us directly.',
        type: 'error'
      });
      setShowPopup(true);
    } finally {
      setRegLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <div className="training-page">
      {/* SUCCESS/ERROR POPUP */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className={`popup-content ${popupMessage.type}`} onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">
              {popupMessage.type === 'success' ? '✅' : '❌'}
            </div>
            <h2>{popupMessage.title}</h2>
            <p>{popupMessage.message}</p>
            <button className="popup-btn" onClick={closePopup}>
              {popupMessage.type === 'success' ? 'Continue' : 'Try Again'}
            </button>
          </div>
        </div>
      )}

      {/* REGISTRATION MODAL */}
      {showRegistration && selectedTraining && (
        <div className="registration-overlay" onClick={closeRegistration}>
          <div className="registration-modal" onClick={(e) => e.stopPropagation()}>
            <div className="registration-header">
              <h2>Register for Training</h2>
              <button className="close-modal" onClick={closeRegistration}>✕</button>
            </div>
            <p className="registration-training-title">{selectedTraining.title}</p>
            <p className="registration-training-meta">
              📅 {selectedTraining.date} • 📍 {selectedTraining.venue} • 💰 {selectedTraining.fee}
            </p>

            <form className="registration-form" onSubmit={handleRegSubmit}>
              <div className="reg-row">
                <div className="reg-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={regData.fullName}
                    onChange={handleRegChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div className="reg-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={regData.email}
                    onChange={handleRegChange}
                    required
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="reg-row">
                <div className="reg-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={regData.phone}
                    onChange={handleRegChange}
                    required
                    placeholder="+234 800 000 0000"
                  />
                </div>
                <div className="reg-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="country"
                    value={regData.country}
                    onChange={handleRegChange}
                    required
                    placeholder="e.g., Nigeria"
                  />
                </div>
              </div>

              <div className="reg-row">
                <div className="reg-group">
                  <label>Institution / Organization *</label>
                  <input
                    type="text"
                    name="institution"
                    value={regData.institution}
                    onChange={handleRegChange}
                    required
                    placeholder="Enter institution name"
                  />
                </div>
                <div className="reg-group">
                  <label>Sector *</label>
                  <select
                    name="sector"
                    value={regData.sector}
                    onChange={handleRegChange}
                    required
                  >
                    <option value="">Select sector</option>
                    <option value="Private Sector">Private Sector</option>
                    <option value="Government Agency">Government Agency</option>
                    <option value="International Agency">International Agency</option>
                    <option value="NGO / Civil Society">NGO / Civil Society</option>
                    <option value="Academia">Academia</option>
                    <option value="Diplomatic Corps">Diplomatic Corps</option>
                    <option value="Financial Institution">Financial Institution</option>
                    <option value="Energy / Power">Energy / Power</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="reg-row">
                <div className="reg-group">
                  <label>Institution Contact Person *</label>
                  <input
                    type="text"
                    name="institutionContactPerson"
                    value={regData.institutionContactPerson}
                    onChange={handleRegChange}
                    required
                    placeholder="Name of contact person"
                  />
                </div>
                <div className="reg-group">
                  <label>Your Position *</label>
                  <input
                    type="text"
                    name="position"
                    value={regData.position}
                    onChange={handleRegChange}
                    required
                    placeholder="e.g., Director, Manager"
                  />
                </div>
              </div>

              <div className="reg-checkbox">
                <label className="reg-checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={regData.agreeTerms}
                    onChange={handleRegChange}
                    required
                  />
                  I confirm that the information provided is accurate and agree to the terms and conditions. *
                </label>
              </div>

              <div className="reg-actions">
                <button type="button" className="reg-cancel-btn" onClick={closeRegistration}>
                  Cancel
                </button>
                <button type="submit" className="reg-submit-btn" disabled={regLoading}>
                  {regLoading ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="training-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(43, 15, 5, 0.9) 0%, rgba(74, 51, 38, 0.75) 50%, rgba(43, 15, 5, 0.9) 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="training-hero-overlay"></div>
        <div className="training-hero-content">
          <span className="training-badge">ABVIG TRAINING</span>
          <h1>Project Development & Financing</h1>
          <p className="training-subtitle">Green Investment Training Workshop</p>
          <div className="training-hero-stats" ref={statsRef}>
            <div className="stat-item">
              <span className="stat-number stat-date">7-12</span>
              <span className="stat-label">December 2026</span>
              <div className="stat-glow"></div>
            </div>
            <div className="stat-item stat-dossiers">
              <span className="stat-number">{counters.dossiers}+</span>
              <span className="stat-label">Investment Dossiers</span>
              <div className="stat-glow"></div>
              <div className="stat-pulse"></div>
            </div>
            <div className="stat-item stat-sectors">
              <span className="stat-number">{counters.sectors}</span>
              <span className="stat-label">Key Sectors</span>
              <div className="stat-glow"></div>
              <div className="stat-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Training List - Accordion Style */}
      <section className="training-list-section">
        <div className="container">
          <h2 className="section-title">Upcoming Training Programs</h2>
          
          {trainings.map((training, index) => (
            <div className={`training-accordion ${activeTraining === index ? 'active' : ''}`} key={training.id}>
              <div className="training-accordion-header" onClick={() => toggleTraining(index)}>
                <div className="accordion-left">
                  <span className="accordion-number">{String(index + 1).padStart(2, '0')}</span>
                  <div className="accordion-title">
                    <h3>{training.title}</h3>
                    <p className="accordion-date">{training.date} • {training.venue}</p>
                  </div>
                </div>
                <span className="accordion-icon">{activeTraining === index ? '−' : '+'}</span>
              </div>
              
              {activeTraining === index && (
                <div className="training-accordion-content">
                  <div className="training-detail-block">
                    <div className="detail-icon">🎯</div>
                    <div className="detail-content">
                      <h4>Strategic Objective</h4>
                      <p>{training.strategicObjective}</p>
                    </div>
                  </div>

                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-icon">📍</span>
                      <div>
                        <h4>Venue</h4>
                        <p>{training.venue}</p>
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">📅</span>
                      <div>
                        <h4>Date</h4>
                        <p>{training.date}</p>
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">💰</span>
                      <div>
                        <h4>Training Fee</h4>
                        <p className="fee-amount">{training.fee}</p>
                      </div>
                    </div>
                  </div>

                  <div className="included-section">
                    <h4>📋 What's Included</h4>
                    <div className="included-grid">
                      {training.includes.map((item, idx) => (
                        <span className="included-tag" key={idx}>✓ {item}</span>
                      ))}
                    </div>
                  </div>

                  <div className="facilitators-section">
                    <h4>👨‍🏫 Facilitators</h4>
                    <div className="facilitators-grid">
                      {training.facilitators.map((facilitator, idx) => (
                        <span className="facilitator-tag" key={idx}>{facilitator}</span>
                      ))}
                    </div>
                  </div>

                  <div className="participants-section">
                    <h4>👥 Target Participants</h4>
                    <div className="participants-grid">
                      {training.participants.map((participant, idx) => (
                        <span className="participant-tag" key={idx}>{participant}</span>
                      ))}
                    </div>
                  </div>

                  <div className="schedule-section">
                    <h4>📅 Program Schedule</h4>
                    <div className="schedule-timeline">
                      {training.schedule.map((item, idx) => (
                        <div className="schedule-item" key={idx}>
                          <div className="schedule-day">{item.day}</div>
                          <div className="schedule-activity">{item.activity}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="sectors-outcomes-grid">
                    <div className="sectors-box">
                      <h4>🎯 Targeted Sectors</h4>
                      <div className="sector-tags">
                        {training.sectors.map((sector, idx) => (
                          <span className="sector-tag" key={idx}>{sector}</span>
                        ))}
                      </div>
                    </div>
                    <div className="outcomes-box">
                      <h4>📊 Expected Outcomes</h4>
                      <p>{training.outcomes}</p>
                    </div>
                  </div>

                  <div className="training-register">
                    <button className="btn-gold" onClick={() => openRegistration(training)}>
                      Register Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="training-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Participate?</h2>
            <p>Register your interest for the Africa-Continental Project Development & Financing Training.</p>
            <button className="btn-gold" onClick={() => openRegistration(trainings[0])}>
              Register Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpcomingTraining;